import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './BankingList_Print.module.css';
import downtriangle from '../../img/down-triangle.png';
import { saveAs } from 'file-saver';
import { utils, write } from 'xlsx';

// 1. 유틸리티 함수: 컴포넌트 외부로 분리하여 렌더링 시마다 재선언 방지
const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ""; // 유효하지 않은 날짜 처리

    const pad = (n, s = 2) => String(n).padStart(s, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

function BankingList_Print() {
    const [organoidList, setOrganoidList] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [showDetails, setShowDetails] = useState({});
    const [selectAll, setSelectAll] = useState({});

    // 데이터 로드
    useEffect(() => {
        const reqOption = {
            method: "post",
            headers: { "content-type": "application/json" },
        };

        fetch("/api/getOrganoidList_All", reqOption)
            .then((response) => response.json())
            .then(data => setOrganoidList(data))
            .catch(err => console.error("데이터 로드 중 오류 발생:", err));
    }, []);

    // 2. 그룹화 로직 최적화: organoidList가 변경될 때만 재계산 (성능 최적화)
    const groupedOrganoidList = useMemo(() => {
        return organoidList.reduce((grouped, item) => {
            const key = item.sampleSource === 'PDX' ? 'PDXO' : 'PDO';
            const type = item.Type || 'Unknown'; // Type이 없을 경우 대비
            
            if (!grouped[key]) grouped[key] = {};
            if (!grouped[key][type]) grouped[key][type] = [];
            
            grouped[key][type].push(item);
            return grouped;
        }, {});
    }, [organoidList]);

    // 3. 핸들러 함수들 최적화 (useCallback 사용)
    const handleCheckboxChange = useCallback((key, isChecked) => {
        setSelectedKeys(prevKeys => 
            isChecked ? [...prevKeys, key] : prevKeys.filter(k => k !== key)
        );
    }, []);

    const handleSelectAllChange = useCallback((keys, key) => {
        setSelectAll(prevState => {
            const isChecked = !prevState[key];
            if (isChecked) {
                // 중복 제거하며 합치기
                setSelectedKeys(prevKeys => [...new Set([...prevKeys, ...keys])]);
            } else {
                // 해당 그룹의 키들만 제거
                setSelectedKeys(prevKeys => prevKeys.filter(k => !keys.includes(k)));
            }
            return { ...prevState, [key]: isChecked };
        });
    }, []);

    const handleToggleDetails = useCallback((key) => {
        setShowDetails(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);

    // 4. Submit 로직 개선 (기존 로직 유지 + 가독성 최적화)
    const handleSubmit = async () => {
        if (selectedKeys.length === 0) {
            return alert("선택된 항목이 없습니다.");
        }

        const reqOption = {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(selectedKeys)
        };

        try {
            const response = await fetch("/api/getBankingListByKey", reqOption);
            const data = await response.json();

            // 데이터 가공 로직 (기존 로직 100% 반영)
            const processedData = data.map(element => {
                const formattedTime = formatDate(element.time);
                // URL 생성 및 공백 처리
                let url = `https://banking.gradiantbio.com/organoid/${element.Organoid_Info_Key}/bank/${formattedTime}`;
                
                return {
                    ...element,
                    time: formattedTime,
                    url: url.replace(/\s/g, '%20'), // 전체 공백 인코딩
                    Organoid_Info_Key: element.Organoid_Info_Key.replace(/hashmark/g, '#') // 전역 치환
                };
            });

            // 엑셀 파일 생성 및 다운로드
            const ws = utils.json_to_sheet(processedData);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, "Sheet1");

            const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
            saveAs(new Blob([wbout], { type: "application/octet-stream" }), 'banking_data.xlsx');
        } catch (err) {
            console.error("제출 중 오류 발생:", err);
            alert("요청 중 오류가 발생했습니다.");
        }
    };

    return (
        <div className={styles.container}>
            {Object.entries(groupedOrganoidList).map(([sampleType, types]) => (
                <div key={sampleType}>
                    <h2>{sampleType}</h2>
                    {Object.entries(types).map(([type, items]) => {
                        const key = `${sampleType}_${type}`;
                        return (
                            <div key={type}>
                                <div className={styles.headerRow}> {/* 정렬을 위해 행 구조 제안 */}
                                    <span onClick={() => handleToggleDetails(key)} className={styles.header}>
                                        <img 
                                            src={downtriangle} 
                                            alt="toggle" 
                                            className={`${styles.toggleImage} ${showDetails[key] ? styles.rotated : ''}`} 
                                        />
                                        {type}
                                    </span>
                                    <input
                                        type="checkbox"
                                        onChange={() => handleSelectAllChange(items.map(item => item.Key), key)}
                                        checked={!!selectAll[key]}
                                    />
                                </div>

                                {showDetails[key] && (
                                    <div className={styles.detailsList}>
                                        {items.map(({ Key }) => (
                                            <div key={Key} className={styles.detailItem}>
                                                <input
                                                    type="checkbox"
                                                    onChange={e => handleCheckboxChange(Key, e.target.checked)}
                                                    checked={selectedKeys.includes(Key)}
                                                />
                                                {Key.replace(/hashmark/g, '#')}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
            <button onClick={handleSubmit} className={styles.submitBtn}>Submit</button>
        </div>
    );
}

export default BankingList_Print;